/**
 * My Course Groups Page
 *
 * Provides a view for teachers and parents to see their associated course groups.
 * - Teachers: See assigned course groups, students, materials, answers, and can post feedback
 * - Parents: See children's course groups, answers (after Sunday), and approved feedback
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  listMyCourseGroups,
  getCourseGroupDetail,
  getStudentList,
  formatCourseGroupName,
  formatWeek
} from '../../api/courseGroupViewApi';
import { getYearLabel, isSpecialTerm, getTermLabel } from '../../api/courseGroupPortalApi';
import { listMaterials, recordAccess, checkAccess } from '../../api/courseGroupMaterialApi';
import { v4 as uuidv4 } from 'uuid';
import { listFeedback, createFeedback, getImageUploadUrls, uploadFeedbackImage } from '../../api/feedbackPortalApi';
import MaterialPdfViewerModal from '../../components/Portal/MaterialPdfViewerModal';
import FeedbackImageUpload from '../../components/Portal/FeedbackImageUpload';
import FeedbackImageGallery from '../../components/Portal/FeedbackImageGallery';
import { media } from '../../styles/theme';

// Styled components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  ${media.phone} {
    padding: 16px 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  ${media.phone} {
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  color: #1f2937;

  ${media.phone} {
    font-size: 20px;
  }
`;

const RoleBadge = styled.span`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$color || '#e0e7ff'};
  color: ${props => props.$textColor || '#3730a3'};
`;

const CourseGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const CourseGroupCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  padding: 16px;
  color: white;

  h3 {
    margin: 0 0 4px 0;
    font-size: 18px;
  }

  .meta {
    font-size: 13px;
    opacity: 0.9;
  }
`;

const CardBody = styled.div`
  padding: 16px;
`;

const StatRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
`;

const Stat = styled.div`
  flex: 1;
  text-align: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;

  .value {
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
  }

  .label {
    font-size: 11px;
    color: #6b7280;
    text-transform: uppercase;
  }
`;

const WeekBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$isHoliday ? '#fef3c7' : '#dbeafe'};
  color: ${props => props.$isHoliday ? '#92400e' : '#1e40af'};
`;

const SlotBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f3e8ff;
  color: #7c3aed;
`;

// Detail View Components
const DetailContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const DetailHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  padding: 20px 24px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  ${media.phone} {
    padding: 16px;
    flex-wrap: wrap;

    h2, h3 { font-size: 18px; }
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;

  /* 手机上 tab 过多时横向滚动，而不是挤成一团/溢出 */
  ${media.phone} {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Tab = styled.button`
  padding: 14px 24px;
  border: none;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#3b82f6' : '#6b7280'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? '#3b82f6' : 'transparent'};
  margin-bottom: -1px;

  &:hover {
    color: #3b82f6;
  }

  ${media.phone} {
    padding: 14px 18px;
    white-space: nowrap;
    min-height: 48px;
  }
`;

const TabContent = styled.div`
  padding: 24px;

  ${media.phone} {
    padding: 16px 12px;
  }
`;

const MaterialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const MaterialCard = styled.div`
  background: ${props => props.$isAnswer ? '#fefce8' : '#f0fdf4'};
  border: 1px solid ${props => props.$isAnswer ? '#fef08a' : '#bbf7d0'};
  border-radius: 8px;
  padding: 16px;
  opacity: ${props => props.$accessible ? 1 : 0.6};
`;

const MaterialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;

  h4 {
    margin: 0;
    font-size: 14px;
    color: #1f2937;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$bg || '#dbeafe'};
  color: ${props => props.$color || '#1e40af'};
`;

const MaterialMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  background: ${props => props.$color || '#3b82f6'};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* 手机上放大触摸目标（看答案/查看等关键按钮） */
  ${media.phone} {
    padding: 11px 16px;
    font-size: 14px;
    min-height: 44px;
  }
`;

const StudentTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }

  th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
  }

  td {
    font-size: 14px;
    color: #374151;
  }
`;

const FeedbackCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const FeedbackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const FeedbackContent = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
`;

const FeedbackMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
  font-size: 14px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  color: #dc2626;
  margin-bottom: 16px;
`;

// Feedback Create Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;

  /* 手机上近全屏，底部按钮始终可达 */
  ${media.phone} {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  /* 让内容区在 flex 弹窗里正常收缩滚动，底部 Footer 始终可见（尤其手机全屏时） */
  flex: 1 1 auto;
  min-height: 0;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
`;

// Grouping controls
const GroupingControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const GroupByLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const GroupBySelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

// Accordion components
const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AccordionPanel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
`;

const AccordionCount = styled.span`
  padding: 4px 10px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const AccordionIcon = styled.span`
  font-size: 14px;
  color: #6b7280;
  transition: transform 0.2s;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div`
  max-height: ${props => props.$expanded ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const AccordionInner = styled.div`
  padding: 20px;
`;

// Schedule display on card
const ScheduleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const ScheduleTag = styled.span`
  padding: 4px 8px;
  background: #f3f4f6;
  color: #374151;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

// Short day of week labels for card display
const SHORT_DAYS = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun'
};

const formatScheduleShort = (schedule) => {
  if (!schedule || !schedule.dayOfWeek) return '';
  const day = SHORT_DAYS[schedule.dayOfWeek] || '';
  return `${day} ${schedule.startTime}-${schedule.endTime}`;
};

// Helper function to get sorted slots with visual indices (for display)
const getSortedSlotsWithVisualIndex = (slots) => {
  if (!slots || slots.length === 0) return [];
  return [...slots]
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    })
    .map((slot, idx) => ({ ...slot, visualIndex: idx + 1 }));
};

// Helper function to get visual index for a specific slotIndex
const getVisualIndex = (slots, slotIndex) => {
  const sorted = getSortedSlotsWithVisualIndex(slots);
  const found = sorted.find(s => s.slotIndex === slotIndex);
  return found ? found.visualIndex : null;
};

// Format slot info for display - converts internal slotIndex to visual index
const formatSlotInfo = (slotInfo, slots) => {
  if (!slotInfo) return 'No slots';
  const totalSlots = slotInfo.totalSlots || 0;
  const currentSlotIndex = slotInfo.currentSlotIndex;
  if (currentSlotIndex) {
    const visualIndex = getVisualIndex(slots, currentSlotIndex);
    return `Slot #${visualIndex || currentSlotIndex}`;
  }
  return `${totalSlots} slots`;
};

const MyCourseGroups = ({ role: roleProp }) => {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseGroups, setCourseGroups] = useState([]);
  const [userRole, setUserRole] = useState(null);

  // Detail view state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('materials');
  const [detailData, setDetailData] = useState(null);
  const [students, setStudents] = useState([]);
  const [materials, setMaterials] = useState({ materials: [], answers: [], currentWeek: 0, teacherSubjectIds: null, message: null });
  const [feedback, setFeedback] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // PDF Viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfMaterialName, setPdfMaterialName] = useState('');
  // 教材 id 传给查看器 → 老师的手写笔记按「用户 × 教材」存到云端
  const [pdfMaterialId, setPdfMaterialId] = useState(null);

  // Material access info for teachers (shows remaining views)
  // Format: { [materialId]: { remaining: number, limit: number } }
  const [materialAccessInfo, setMaterialAccessInfo] = useState({});

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    type: 'group',
    studentId: '',
    term: 1,
    week: 1,
    content: '',
    images: []
  });
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Grouping state (for teachers)
  const [groupBy, setGroupBy] = useState('campus'); // 'campus' or 'year'
  const [expandedGroups, setExpandedGroups] = useState({});

  // Get user role from prop or Cognito groups
  useEffect(() => {
    if (roleProp) {
      setUserRole(roleProp);
    } else if (auth.user) {
      const groups = auth.user.profile['cognito:groups'] || [];
      if (groups.includes('portal_teacher')) {
        setUserRole('teacher');
      } else if (groups.includes('portal_parent')) {
        setUserRole('parent');
      }
    }
  }, [auth.user, roleProp]);

  // Determine the viewAs value from roleProp or userRole
  const viewAs = roleProp || userRole;

  // Load course groups
  const loadCourseGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await listMyCourseGroups(viewAs);
      setCourseGroups(result.courseGroups || []);
      // Only let backend override role if no roleProp set
      if (!roleProp && result.role) setUserRole(result.role);
    } catch (err) {
      console.error('Error loading course groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [viewAs, roleProp]);

  useEffect(() => {
    loadCourseGroups();
  }, [loadCourseGroups]);

  // Group course groups by selected criteria (for teachers)
  const groupedCourseGroups = useMemo(() => {
    if (!courseGroups || courseGroups.length === 0) return {};

    const groups = {};

    courseGroups.forEach(group => {
      let key, label;

      if (groupBy === 'campus') {
        key = group.campusId || 'unknown';
        label = group.campusName || 'Unknown Campus';
      } else {
        // Group by year
        key = String(group.year ?? 0);
        label = getYearLabel(group.year) || 'Unknown Year';
      }

      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          items: []
        };
      }
      groups[key].items.push(group);
    });

    return groups;
  }, [courseGroups, groupBy]);

  // Sort groups by label
  const sortedGroupKeys = useMemo(() => {
    return Object.keys(groupedCourseGroups).sort((a, b) => {
      const groupA = groupedCourseGroups[a];
      const groupB = groupedCourseGroups[b];

      if (groupBy === 'year') {
        return parseInt(a) - parseInt(b); // Numerical sort for year
      }
      return groupA.label.localeCompare(groupB.label); // Alphabetical for campus
    });
  }, [groupedCourseGroups, groupBy]);

  // Sort items within each group: by campus name, then year, then subject
  const getSortedItems = (items) => {
    return [...items].sort((a, b) => {
      // Sort by campus name first
      const campusCompare = (a.campusName || '').localeCompare(b.campusName || '');
      if (campusCompare !== 0) return campusCompare;

      // Then by year
      const yearCompare = (a.year || 0) - (b.year || 0);
      if (yearCompare !== 0) return yearCompare;

      // Then by title
      return (a.title || '').localeCompare(b.title || '');
    });
  };

  // Initialize expanded state when groups change
  useEffect(() => {
    if (Object.keys(groupedCourseGroups).length > 0 && Object.keys(expandedGroups).length === 0) {
      const initialExpanded = {};
      Object.keys(groupedCourseGroups).forEach(key => {
        initialExpanded[key] = true; // All expanded by default
      });
      setExpandedGroups(initialExpanded);
    }
  }, [groupedCourseGroups, expandedGroups]);

  // Toggle accordion panel
  const toggleAccordion = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Handle grouping method change
  const handleGroupByChange = (e) => {
    setGroupBy(e.target.value);
    setExpandedGroups({}); // Reset expanded state
  };

  // Load detail data when a group is selected
  const loadDetailData = useCallback(async (groupId) => {
    setDetailLoading(true);
    setMaterialAccessInfo({}); // Reset access info

    try {
      const [detail, materialsData, feedbackData] = await Promise.all([
        getCourseGroupDetail(groupId, viewAs),
        listMaterials(groupId, viewAs),
        listFeedback(groupId, viewAs)
      ]);

      setDetailData(detail);
      setMaterials(materialsData);
      setFeedback(feedbackData);

      // Load students for teachers
      if (userRole === 'teacher') {
        try {
          const studentsData = await getStudentList(groupId);
          setStudents(studentsData);
        } catch (err) {
          console.warn('Could not load students:', err);
        }

        // Load access info for accessible materials (not answers)
        const accessibleMaterials = (materialsData.materials || []).filter(m => m.accessible);
        if (accessibleMaterials.length > 0) {
          try {
            const accessInfoPromises = accessibleMaterials.map(m =>
              checkAccess(groupId, m.id, 'VIEW')
                .then(info => ({ materialId: m.id, ...info }))
                .catch(() => ({ materialId: m.id, remaining: 3, limit: 3 }))
            );
            const accessInfoResults = await Promise.all(accessInfoPromises);
            const accessInfoMap = {};
            accessInfoResults.forEach(info => {
              accessInfoMap[info.materialId] = {
                remaining: info.remaining,
                limit: info.limit
              };
            });
            setMaterialAccessInfo(accessInfoMap);
          } catch (err) {
            console.warn('Could not load access info:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error loading detail:', err);
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, [userRole, viewAs]);

  // Select a course group
  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setActiveTab(userRole === 'parent' ? 'answers' : 'materials');
    loadDetailData(group.id);
  };

  // Deep link: /portal/my-course-groups?group=<id> opens that class straight away.
  //
  // The timetable links here so a teacher can go from "what am I teaching at 5?" to that
  // class's materials in one tap. Runs once per mount and then strips the parameter, so
  // pressing Back out of the detail returns to the list instead of reopening the class.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || loading) return;
    const wanted = searchParams.get('group');
    if (!wanted) return;
    deepLinkHandled.current = true;

    const match = courseGroups.find(group => group.id === wanted);
    if (match) {
      setSelectedGroup(match);
      setActiveTab(userRole === 'parent' ? 'answers' : 'materials');
      loadDetailData(match.id);
    }

    const next = new URLSearchParams(searchParams);
    next.delete('group');
    setSearchParams(next, { replace: true });
  }, [loading, courseGroups, userRole, loadDetailData, searchParams, setSearchParams]);

  // Go back to list
  const handleBack = () => {
    setSelectedGroup(null);
    setDetailData(null);
    setStudents([]);
    setMaterials({ materials: [], answers: [], currentWeek: 0, teacherSubjectIds: null, message: null });
    setFeedback([]);
  };

  // View material
  const handleViewMaterial = async (material, isAnswer) => {
    if (!material.accessible) {
      alert(materials.digitalMaterialBlocked
        ? 'Digital materials are turned off for your account. Please contact your campus manager.'
        : 'This material is not accessible at this time.');
      return;
    }

    try {
      const result = await recordAccess(selectedGroup.id, material.id, 'VIEW');
      // Use PDF Viewer Modal instead of window.open
      setPdfUrl(result.url);
      setPdfMaterialName(material.name);
      setPdfMaterialId(material.id);
      setShowPdfViewer(true);

      // Update remaining count for materials (not answers)
      if (!isAnswer && result.remaining !== undefined && result.remaining !== -1) {
        setMaterialAccessInfo(prev => ({
          ...prev,
          [material.id]: {
            remaining: result.remaining,
            limit: result.limit
          }
        }));
      }
    } catch (err) {
      alert(`Failed to access material: ${err.message}`);
    }
  };

  // Close PDF viewer
  const closePdfViewer = () => {
    setShowPdfViewer(false);
    setPdfUrl(null);
    setPdfMaterialName('');
    setPdfMaterialId(null);
  };

  // Create feedback
  const handleCreateFeedback = async () => {
    if (!feedbackForm.content.trim()) {
      alert('Please enter feedback content');
      return;
    }

    setSavingFeedback(true);

    try {
      const feedbackId = uuidv4();
      let imagesData = [];

      // Step 1: Upload images first (if any)
      if (feedbackForm.images.length > 0) {
        const imageMetadata = feedbackForm.images.map(img => ({
          fileName: img.file.name,
          contentType: img.file.type
        }));

        const uploadUrls = await getImageUploadUrls(selectedGroup.id, feedbackId, imageMetadata);

        // Upload all images in parallel
        await Promise.all(uploadUrls.map((urlInfo, i) =>
          uploadFeedbackImage(urlInfo.uploadUrl, feedbackForm.images[i].file, urlInfo.contentType)
        ));

        imagesData = uploadUrls.map((urlInfo, i) => ({
          s3Key: urlInfo.s3Key,
          name: feedbackForm.images[i].name || feedbackForm.images[i].file.name
        }));
      }

      // Step 2: Create feedback with everything in one call
      await createFeedback({
        id: feedbackId,
        courseGroupId: selectedGroup.id,
        type: feedbackForm.type,
        studentId: feedbackForm.type === 'individual' ? feedbackForm.studentId : undefined,
        term: feedbackForm.term,
        week: feedbackForm.week,
        content: feedbackForm.content,
        ...(imagesData.length > 0 && { images: imagesData })
      });

      setShowFeedbackModal(false);
      setFeedbackForm({ type: 'group', studentId: '', term: selectedGroup?.term ?? 1, week: 1, content: '', images: [] });

      // Reload feedback
      const feedbackData = await listFeedback(selectedGroup.id);
      setFeedback(feedbackData);
    } catch (err) {
      alert(`Failed to create feedback: ${err.message}`);
    } finally {
      setSavingFeedback(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>Loading your course groups...</LoadingState>
      </Container>
    );
  }

  // Detail View
  if (selectedGroup) {
    return (
      <Container>
        <DetailContainer>
          <DetailHeader>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px' }}>
                {formatCourseGroupName(selectedGroup)}
              </h2>
              <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                {selectedGroup.campusName} | {isSpecialTerm(selectedGroup.term)
                  ? formatSlotInfo(detailData?.courseGroup?.currentSlotInfo, detailData?.courseGroup?.slots)
                  : formatWeek(detailData?.courseGroup?.currentWeek, detailData?.courseGroup?.isHoliday)}
              </div>
            </div>
            <BackButton onClick={handleBack}>Back to List</BackButton>
          </DetailHeader>

          <TabContainer>
            {userRole !== 'parent' && (
              <Tab $active={activeTab === 'materials'} onClick={() => setActiveTab('materials')}>
                Materials ({materials.materials.length})
              </Tab>
            )}
            <Tab $active={activeTab === 'answers'} onClick={() => setActiveTab('answers')}>
              Answers ({materials.answers.length})
            </Tab>
            {userRole === 'teacher' && (
              <Tab $active={activeTab === 'students'} onClick={() => setActiveTab('students')}>
                Students ({students.length})
              </Tab>
            )}
            <Tab $active={activeTab === 'feedback'} onClick={() => setActiveTab('feedback')}>
              Feedback ({feedback.length})
            </Tab>
          </TabContainer>

          <TabContent>
            {detailLoading ? (
              <LoadingState>Loading...</LoadingState>
            ) : (
              <>
                {activeTab === 'materials' && (
                  <>
                    {materials.digitalMaterialBlocked && (
                      <div style={{
                        padding: '16px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#b91c1c', marginBottom: '4px' }}>
                          Digital Materials Disabled
                        </div>
                        <div style={{ fontSize: '13px', color: '#dc2626' }}>
                          Your account is set to paper copies only, so materials and
                          answers cannot be opened here. Please contact your campus
                          manager if you believe this is a mistake.
                        </div>
                      </div>
                    )}
                    {userRole === 'teacher' && materials.teacherSubjectIds !== null && materials.teacherSubjectIds.length === 0 ? (
                      <div style={{
                        padding: '16px',
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                          No Subjects Assigned
                        </div>
                        <div style={{ fontSize: '13px', color: '#a16207' }}>
                          {materials.message || 'You have not been assigned to any subjects in this course group. Please contact the administrator to be assigned to subjects before accessing materials.'}
                        </div>
                      </div>
                    ) : userRole === 'teacher' && (
                      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
                        {isSpecialTerm(selectedGroup?.term) ? (
                          <>
                            You can access materials from your previous, current, and next assigned class slots
                            {materials.teacherSubjectIds && materials.teacherSubjectIds.length > 0 && (
                              <span> (filtered by your assigned subjects)</span>
                            )}
                          </>
                        ) : (
                          <>
                            You can access the material for your current teaching session, plus the one before and the one after, for your assigned schedules (holiday weeks are skipped)
                            {materials.teacherSubjectIds && materials.teacherSubjectIds.length > 0 && (
                              <span> (also filtered by your assigned subjects)</span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {userRole === 'parent' && (
                      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
                        Materials are not visible to parents. Please see the Answers tab for answer sheets.
                      </div>
                    )}
                    {materials.materials.length === 0 ? (
                      <EmptyState>
                        {userRole === 'teacher' && materials.teacherSubjectIds !== null && materials.teacherSubjectIds.length === 0
                          ? 'No materials visible - you have not been assigned to any subjects.'
                          : 'No materials assigned to this course group.'}
                      </EmptyState>
                    ) : (
                      <MaterialGrid>
                        {materials.materials.map(material => {
                          const accessInfo = materialAccessInfo[material.id];
                          const remaining = accessInfo?.remaining ?? 3;
                          const showRemaining = userRole === 'teacher' && material.accessible && remaining !== -1;

                          return (
                            <MaterialCard key={material.id} $accessible={material.accessible}>
                              <MaterialHeader>
                                <h4>{material.name}</h4>
                                <Badge>
                                  {material.assignedTerm != null
                                    ? `T${material.assignedTerm}·W${material.assignedWeek ?? material.week}`
                                    : `W${material.assignedWeek ?? material.week}`}
                                </Badge>
                              </MaterialHeader>
                              <MaterialMeta>
                                {material.subjectName || '-'} | Y{material.year}T{material.term}
                              </MaterialMeta>
                              {showRemaining && (
                                <div style={{
                                  fontSize: '12px',
                                  color: remaining === 0 ? '#dc2626' : '#6b7280',
                                  marginBottom: '8px'
                                }}>
                                  {remaining} {remaining === 1 ? 'view' : 'views'} remaining
                                  {remaining > 0 && (
                                    <span style={{ color: '#9ca3af', marginLeft: '4px' }}>
                                      (2h window)
                                    </span>
                                  )}
                                </div>
                              )}
                              <Button
                                onClick={() => handleViewMaterial(material, false)}
                                disabled={!material.accessible}
                              >
                                {material.accessible ? 'View' : 'Not Available'}
                              </Button>
                            </MaterialCard>
                          );
                        })}
                      </MaterialGrid>
                    )}
                  </>
                )}

                {activeTab === 'answers' && (
                  <>
                    {userRole === 'teacher' && materials.teacherSubjectIds !== null && materials.teacherSubjectIds.length === 0 && (
                      <div style={{
                        padding: '16px',
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                          No Subjects Assigned
                        </div>
                        <div style={{ fontSize: '13px', color: '#a16207' }}>
                          You have not been assigned to any subjects in this course group. Please contact the administrator.
                        </div>
                      </div>
                    )}
                    {userRole === 'parent' && (
                      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
                        Answers for current and past weeks/sessions are available to view.
                      </div>
                    )}
                    {materials.answers.length === 0 ? (
                      <EmptyState>
                        {userRole === 'teacher' && materials.teacherSubjectIds !== null && materials.teacherSubjectIds.length === 0
                          ? 'No answers visible - you have not been assigned to any subjects.'
                          : 'No answers assigned to this course group.'}
                      </EmptyState>
                    ) : (
                      <MaterialGrid>
                        {materials.answers.map(answer => (
                          <MaterialCard key={answer.id} $isAnswer $accessible={answer.accessible}>
                            <MaterialHeader>
                              <h4>{answer.name}</h4>
                              <Badge $bg="#fef9c3" $color="#854d0e">
                                {answer.assignedTerm != null
                                  ? `T${answer.assignedTerm}·W${answer.assignedWeek ?? answer.week}`
                                  : `W${answer.assignedWeek ?? answer.week}`}
                              </Badge>
                            </MaterialHeader>
                            <MaterialMeta>
                              {answer.subjectName || '-'} | Y{answer.year}T{answer.term}
                            </MaterialMeta>
                            <Button
                              $color="#eab308"
                              onClick={() => handleViewMaterial(answer, true)}
                              disabled={!answer.accessible}
                            >
                              {answer.accessible ? 'View Answer' : 'Not Available Yet'}
                            </Button>
                          </MaterialCard>
                        ))}
                      </MaterialGrid>
                    )}
                  </>
                )}

                {activeTab === 'students' && userRole === 'teacher' && (
                  <>
                    {students.length === 0 ? (
                      <EmptyState>No students enrolled in this course group.</EmptyState>
                    ) : (
                      <StudentTable>
                        <thead>
                          <tr>
                            <th>Name</th>
                            {/* <th>Credit</th> */}
                            {/* <th>Discount</th> */}
                            <th>Start Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map(student => (
                            <tr key={student.id}>
                              <td>{student.name}</td>
                              {/* <td>{student.credit || 0}</td> */}
                              {/* <td>{student.discount || 0}%</td> */}
                              <td>{student.startDate || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </StudentTable>
                    )}
                  </>
                )}

                {activeTab === 'feedback' && (
                  <>
                    {userRole === 'teacher' && (
                      <div style={{ marginBottom: '16px' }}>
                        <Button onClick={() => {
                          // Default Term/Week to the course group's current term/week
                          setFeedbackForm(f => ({
                            ...f,
                            term: selectedGroup?.term ?? 1,
                            week: detailData?.courseGroup?.currentWeek ?? selectedGroup?.currentWeek ?? 1
                          }));
                          setShowFeedbackModal(true);
                        }}>
                          + New Feedback
                        </Button>
                      </div>
                    )}
                    {feedback.length === 0 ? (
                      <EmptyState>No feedback yet.</EmptyState>
                    ) : (
                      feedback.map(fb => (
                        <FeedbackCard key={fb.id}>
                          <FeedbackHeader>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Badge $bg={fb.type === 'group' ? '#dbeafe' : '#e0e7ff'} $color={fb.type === 'group' ? '#1e40af' : '#3730a3'}>
                                {fb.type === 'group' ? 'Group' : fb.studentName || 'Individual'}
                              </Badge>
                              <Badge $bg="#f3f4f6" $color="#374151">
                                {getTermLabel(fb.term) ? `${getTermLabel(fb.term)} · Week ${fb.week}` : `Week ${fb.week}`}
                              </Badge>
                              {fb.approvalStatus === 'pending' && (
                                <Badge $bg="#fef3c7" $color="#92400e">Pending Approval</Badge>
                              )}
                            </div>
                          </FeedbackHeader>
                          <FeedbackContent>{fb.content}</FeedbackContent>
                          {fb.images && fb.images.length > 0 && (
                            <FeedbackImageGallery images={fb.images} />
                          )}
                          <FeedbackMeta>
                            By {fb.teacherName} | {new Date(fb.createdAt).toLocaleDateString()}
                          </FeedbackMeta>
                        </FeedbackCard>
                      ))
                    )}
                  </>
                )}
              </>
            )}
          </TabContent>
        </DetailContainer>

        {/* Feedback Modal */}
        {showFeedbackModal && (
          <ModalOverlay>
            <Modal>
              <ModalHeader>
                <h3>Create Feedback</h3>
              </ModalHeader>
              <ModalContent>
                <FormGroup>
                  <label>Type</label>
                  <Select
                    value={feedbackForm.type}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, type: e.target.value })}
                  >
                    <option value="group">Group Feedback</option>
                    <option value="individual">Individual Student (requires approval)</option>
                  </Select>
                </FormGroup>

                {feedbackForm.type === 'individual' && (
                  <FormGroup>
                    <label>Student</label>
                    <Select
                      value={feedbackForm.studentId}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, studentId: e.target.value })}
                    >
                      <option value="">Select a student...</option>
                      {students.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </Select>
                  </FormGroup>
                )}

                <FormGroup>
                  <label>Term</label>
                  <Select
                    value={feedbackForm.term}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, term: parseInt(e.target.value) })}
                  >
                    <option value={1}>Term 1</option>
                    <option value={2}>Term 2</option>
                    <option value={3}>Term 3</option>
                    <option value={4}>Term 4</option>
                    <option value={0}>Special Term</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <label>Week</label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={feedbackForm.week}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, week: parseInt(e.target.value) || 1 })}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Feedback Content</label>
                  <TextArea
                    value={feedbackForm.content}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, content: e.target.value })}
                    placeholder="Enter your feedback..."
                  />
                </FormGroup>

                <FormGroup>
                  <label>Images (optional, max 10)</label>
                  <FeedbackImageUpload
                    images={feedbackForm.images}
                    onChange={(images) => setFeedbackForm({ ...feedbackForm, images })}
                    disabled={savingFeedback}
                  />
                </FormGroup>
              </ModalContent>
              <ModalFooter>
                <Button $color="#6b7280" onClick={() => setShowFeedbackModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateFeedback} disabled={savingFeedback}>
                  {savingFeedback ? 'Creating...' : 'Create Feedback'}
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}

        {/* PDF Viewer Modal */}
        <MaterialPdfViewerModal
          show={showPdfViewer}
          onClose={closePdfViewer}
          pdfUrl={pdfUrl}
          materialName={pdfMaterialName}
          materialId={pdfMaterialId}
          mode="view"
        />
      </Container>
    );
  }

  // List View
  return (
    <Container>
      <Header>
        <Title>My Course Groups</Title>
        <RoleBadge
          $color={userRole === 'teacher' ? '#dbeafe' : '#dcfce7'}
          $textColor={userRole === 'teacher' ? '#1e40af' : '#166534'}
        >
          {userRole === 'teacher' ? 'Teacher' : 'Parent'}
        </RoleBadge>
      </Header>

      {error && <ErrorState>{error}</ErrorState>}

      {/* Grouping controls - teachers only */}
      {userRole === 'teacher' && courseGroups.length > 0 && (
        <GroupingControls>
          <GroupByLabel>Group by:</GroupByLabel>
          <GroupBySelect value={groupBy} onChange={handleGroupByChange}>
            <option value="campus">Campus</option>
            <option value="year">Year</option>
          </GroupBySelect>
        </GroupingControls>
      )}

      {courseGroups.length === 0 ? (
        <EmptyState>
          {userRole === 'teacher'
            ? 'You have not been assigned to any course groups yet.'
            : 'Your children are not enrolled in any course groups yet.'}
        </EmptyState>
      ) : userRole === 'teacher' ? (
        /* Accordion view for teachers */
        <AccordionContainer>
          {sortedGroupKeys.map(groupKey => {
            const groupData = groupedCourseGroups[groupKey];
            const isExpanded = expandedGroups[groupKey] !== false; // default expanded
            const sortedItems = getSortedItems(groupData.items);

            return (
              <AccordionPanel key={groupKey}>
                <AccordionHeader onClick={() => toggleAccordion(groupKey)}>
                  <AccordionTitle>
                    <h3>{groupData.label}</h3>
                    <AccordionCount>{groupData.items.length} {groupData.items.length === 1 ? 'course' : 'courses'}</AccordionCount>
                  </AccordionTitle>
                  <AccordionIcon $expanded={isExpanded}>▼</AccordionIcon>
                </AccordionHeader>
                <AccordionContent $expanded={isExpanded}>
                  <AccordionInner>
                    <CourseGroupGrid>
                      {sortedItems.map(group => (
                        <CourseGroupCard key={group.id} onClick={() => handleSelectGroup(group)}>
                          <CardHeader>
                            <h3>{formatCourseGroupName(group)}</h3>
                            <div className="meta">{group.campusName}</div>
                          </CardHeader>
                          <CardBody>
                            <StatRow>
                              <Stat>
                                <div className="value">{group.totalStudents || 0}</div>
                                <div className="label">Students</div>
                              </Stat>
                              <Stat>
                                <div className="value">{group.totalMaterials || 0}</div>
                                <div className="label">Materials</div>
                              </Stat>
                              <Stat>
                                {isSpecialTerm(group.term) ? (
                                  <SlotBadge>
                                    {group.slots?.length || 0} slots
                                  </SlotBadge>
                                ) : (
                                  <WeekBadge $isHoliday={group.isHoliday}>
                                    {formatWeek(group.currentWeek, group.isHoliday)}
                                  </WeekBadge>
                                )}
                                <div className="label" style={{ marginTop: '4px' }}>
                                  {isSpecialTerm(group.term) ? 'Sessions' : 'Current'}
                                </div>
                              </Stat>
                            </StatRow>
                            {/* Schedule display (regular term) */}
                            {!isSpecialTerm(group.term) && group.schedules && group.schedules.length > 0 && (
                              <ScheduleList>
                                {group.schedules.map((schedule, idx) => (
                                  <ScheduleTag key={idx}>
                                    {formatScheduleShort(schedule)}
                                  </ScheduleTag>
                                ))}
                              </ScheduleList>
                            )}
                            {/* Special term indicator */}
                            {isSpecialTerm(group.term) && (
                              <Badge $bg="#f3e8ff" $color="#7c3aed" style={{ marginTop: '8px' }}>
                                Special Term
                              </Badge>
                            )}
                            {group.isPrimary && (
                              <Badge $bg="#dcfce7" $color="#166534" style={{ marginTop: '8px' }}>
                                Primary Teacher
                              </Badge>
                            )}
                          </CardBody>
                        </CourseGroupCard>
                      ))}
                    </CourseGroupGrid>
                  </AccordionInner>
                </AccordionContent>
              </AccordionPanel>
            );
          })}
        </AccordionContainer>
      ) : (
        /* Original flat grid for parents */
        <CourseGroupGrid>
          {courseGroups.map(group => (
            <CourseGroupCard key={group.id} onClick={() => handleSelectGroup(group)}>
              <CardHeader>
                <h3>{formatCourseGroupName(group)}</h3>
                <div className="meta">{group.campusName}</div>
              </CardHeader>
              <CardBody>
                <StatRow>
                  <Stat>
                    <div className="value">{group.totalStudents || 0}</div>
                    <div className="label">Students</div>
                  </Stat>
                  <Stat>
                    <div className="value">{group.totalMaterials || 0}</div>
                    <div className="label">Materials</div>
                  </Stat>
                  <Stat>
                    {isSpecialTerm(group.term) ? (
                      <SlotBadge>
                        {group.slots?.length || 0} slots
                      </SlotBadge>
                    ) : (
                      <WeekBadge $isHoliday={group.isHoliday}>
                        {formatWeek(group.currentWeek, group.isHoliday)}
                      </WeekBadge>
                    )}
                    <div className="label" style={{ marginTop: '4px' }}>
                      {isSpecialTerm(group.term) ? 'Sessions' : 'Current'}
                    </div>
                  </Stat>
                </StatRow>
                {group.childName && (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Child: {group.childName}
                  </div>
                )}
                {/* Special term indicator */}
                {isSpecialTerm(group.term) && (
                  <Badge $bg="#f3e8ff" $color="#7c3aed" style={{ marginTop: '8px' }}>
                    Special Term
                  </Badge>
                )}
              </CardBody>
            </CourseGroupCard>
          ))}
        </CourseGroupGrid>
      )}
    </Container>
  );
};

export default MyCourseGroups;
